// Empty State Component - Fully Modular and Production-Ready
// CRITICAL: No store imports to avoid circular dependencies and initialization issues
import React, { useCallback, createContext, useContext, useMemo } from 'react';
import { 
  LucideIcon, 
  FileAudio, 
  Search, 
  Folder, 
  Upload as UploadIcon, 
  FolderPlus, 
  GitBranch, 
  AlertCircle, 
  RefreshCw,
  Loader2,
  WifiOff,
  ServerCrash,
  FileX
} from 'lucide-react';
import { cn } from '@utils/classNames';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type EmptyStateVariant = 'empty' | 'error' | 'loading';
export type ErrorType = 'network' | 'server' | 'notFound' | 'generic';

export interface ActionButton {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  'aria-label'?: string;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

export interface EmptyStateProps {
  // Content
  icon?: LucideIcon;
  title: string;
  description?: string;
  
  // Actions
  action?: ActionButton;
  secondaryAction?: ActionButton;
  
  // Styling
  className?: string;
  variant?: EmptyStateVariant;
  
  // Drag & Drop support
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  isDraggingOver?: boolean;
  
  // Accessibility
  'aria-label'?: string;
  'aria-labelledby'?: string;
  
  // Error state support
  onRetry?: () => void;
  retryLabel?: string;
  errorType?: ErrorType;
  
  // Loading state support
  loadingText?: string;
  
  // i18n support (for future use)
  translations?: Record<string, string>;
}

// ============================================================================
// I18N CONTEXT (Prepared for future i18n implementation)
// ============================================================================

interface I18nContextType {
  t: (key: string, fallback?: string) => string;
  locale: string;
}

const I18nContext = createContext<I18nContextType>({
  t: (key: string, fallback?: string) => fallback || key,
  locale: 'en'
});

export const useI18n = () => useContext(I18nContext);

// ============================================================================
// SUB-COMPONENTS (Modular building blocks)
// ============================================================================

interface EmptyStateIconProps {
  icon?: LucideIcon;
  variant: EmptyStateVariant;
  isDraggingOver: boolean;
  errorType?: ErrorType;
}

const EmptyStateIcon: React.FC<EmptyStateIconProps> = ({ 
  icon, 
  variant, 
  isDraggingOver,
  errorType 
}) => {
  const getIcon = (): LucideIcon | undefined => {
    if (variant === 'loading') return Loader2;
    if (variant === 'error') {
      switch (errorType) {
        case 'network': return WifiOff;
        case 'server': return ServerCrash;
        case 'notFound': return FileX;
        default: return AlertCircle;
      }
    }
    return icon;
  };

  const displayIcon = getIcon();
  if (!displayIcon) return null;

  const iconColorClass = variant === 'error' 
    ? 'text-red-400' 
    : variant === 'loading'
      ? 'text-primary animate-spin'
      : isDraggingOver 
        ? 'text-primary animate-pulse' 
        : 'text-primary';

  const bgColorClass = variant === 'error'
    ? 'bg-red-500/20'
    : variant === 'loading'
      ? 'bg-primary/20'
      : isDraggingOver 
        ? 'bg-primary/30 scale-110' 
        : 'bg-primary/10';

  return (
    <div className={cn(
      "p-6 rounded-full mb-6 transition-all animate-bounce-in",
      bgColorClass
    )}>
      {React.createElement(displayIcon as React.ComponentType<any>, {
        className: cn(
          "w-16 h-16 transition-colors",
          iconColorClass
        ),
        'aria-hidden': true
      })}
    </div>
  );
};

interface EmptyStateTitleProps {
  title: string;
  variant: EmptyStateVariant;
  id?: string;
}

const EmptyStateTitle: React.FC<EmptyStateTitleProps> = ({ title, variant, id }) => {
  const colorClass = variant === 'error' ? 'text-red-300' : 'text-white';
  
  return (
    <h3 
      className={cn(
        "font-dela text-2xl mb-2 animate-slide-in-up [animation-delay:100ms]",
        colorClass
      )}
      id={id}
    >
      {title}
    </h3>
  );
};

interface EmptyStateDescriptionProps {
  description?: string;
  variant: EmptyStateVariant;
}

const EmptyStateDescription: React.FC<EmptyStateDescriptionProps> = ({ 
  description, 
  variant 
}) => {
  if (!description) return null;
  
  const colorClass = variant === 'error' 
    ? 'text-red-400/80' 
    : 'text-white/60';
  
  return (
    <p className={cn(
      "font-inter mb-6 text-center max-w-md animate-slide-in-up [animation-delay:200ms]",
      colorClass
    )}>
      {description}
    </p>
  );
};

interface EmptyStateActionsProps {
  action?: ActionButton;
  secondaryAction?: ActionButton;
  variant: EmptyStateVariant;
  onRetry?: () => void;
  retryLabel?: string;
  onKeyDown: (e: React.KeyboardEvent, onClick: () => void) => void;
}

const EmptyStateActions: React.FC<EmptyStateActionsProps> = ({
  action,
  secondaryAction,
  variant,
  onRetry,
  retryLabel = 'Try again',
  onKeyDown
}) => {
  if (!action && !secondaryAction && !(variant === 'error' && onRetry)) {
    return null;
  }

  const getButtonClasses = (buttonVariant: 'primary' | 'secondary' | 'danger' = 'primary') => {
    const base = "flex items-center justify-center gap-2 px-6 py-3 rounded-[15px] transition-all touch-manipulation min-h-[48px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark";
    
    switch (buttonVariant) {
      case 'danger':
        return cn(
          base,
          "bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 focus:ring-red-500"
        );
      case 'secondary':
        return cn(
          base,
          "px-5 bg-white/10 hover:bg-white/20 active:bg-white/30 focus:ring-primary"
        );
      default:
        return cn(
          base,
          "bg-gradient-to-r from-[#30d870]/80 to-[#00ff61]/80 hover:from-[#30d870] hover:to-[#00ff61] active:scale-95 shadow-lg shadow-primary/20 hover:scale-105 focus:ring-primary"
        );
    }
  };

  const getButtonTextClasses = (buttonVariant: 'primary' | 'secondary' | 'danger' = 'primary') => {
    switch (buttonVariant) {
      case 'danger':
        return "font-codec font-bold text-lg text-red-400";
      case 'secondary':
        return "font-codec font-bold text-lg text-white";
      default:
        return "font-codec font-bold text-xl text-black";
    }
  };

  const isLoading = variant === 'loading';

  return (
    <div className="flex flex-col sm:flex-row gap-3 animate-slide-in-up [animation-delay:300ms]">
      {variant === 'error' && onRetry && (
        <button
          onClick={onRetry}
          onKeyDown={(e) => onKeyDown(e, onRetry)}
          className={getButtonClasses('danger')}
          aria-label={retryLabel}
          disabled={isLoading}
        >
          <RefreshCw className={cn(
            "w-5 h-5 text-red-400",
            isLoading && "animate-spin"
          )} aria-hidden="true" />
          <span className={getButtonTextClasses('danger')}>
            {retryLabel}
          </span>
        </button>
      )}
      
      {secondaryAction && (
        <button
          onClick={secondaryAction.onClick}
          onKeyDown={(e) => onKeyDown(e, secondaryAction.onClick)}
          className={getButtonClasses(secondaryAction.variant || 'secondary')}
          aria-label={secondaryAction['aria-label'] || secondaryAction.label}
          disabled={secondaryAction.disabled || isLoading}
        >
          {secondaryAction.icon && (
            <secondaryAction.icon className="w-5 h-5 text-white" aria-hidden="true" />
          )}
          <span className={getButtonTextClasses(secondaryAction.variant || 'secondary')}>
            {secondaryAction.label}
          </span>
        </button>
      )}
      
      {action && (
        <button
          onClick={action.onClick}
          onKeyDown={(e) => onKeyDown(e, action.onClick)}
          className={getButtonClasses(action.variant || 'primary')}
          aria-label={action['aria-label'] || action.label}
          disabled={action.disabled || isLoading}
        >
          {action.icon ? (
            <action.icon className={cn(
              "w-5 h-5",
              (action.variant === 'primary' || !action.variant) ? "text-black" : "text-white"
            )} aria-hidden="true" />
          ) : (
            <UploadIcon className={cn(
              "w-5 h-5",
              (action.variant === 'primary' || !action.variant) ? "text-black" : "text-white"
            )} aria-hidden="true" />
          )}
          <span className={getButtonTextClasses(action.variant || 'primary')}>
            {action.label}
          </span>
        </button>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const EmptyState: React.FC<EmptyStateProps> = ({ 
  icon, 
  title, 
  description, 
  action,
  secondaryAction,
  className,
  onDragOver,
  onDragLeave,
  onDrop,
  isDraggingOver = false,
  variant = 'empty',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onRetry,
  retryLabel = 'Try again',
  errorType = 'generic',
  loadingText
}) => {
  // i18n hook ready for future use
  // const { t } = useI18n();
  
  // Handle keyboard navigation for buttons
  const handleKeyDown = useCallback((e: React.KeyboardEvent, onClick: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }, []);

  // Get container classes based on variant
  const containerClasses = useMemo(() => {
    return cn(
      "flex flex-col items-center justify-center py-12 px-4 text-center rounded-xl border-2 border-dashed transition-all animate-fade-in",
      variant === 'error'
        ? "bg-red-500/10 border-red-500/30"
        : variant === 'loading'
          ? "bg-dark-secondary/30 border-white/20"
          : isDraggingOver 
            ? "bg-primary/20 border-primary scale-[1.02] shadow-lg shadow-primary/20" 
            : "bg-dark-secondary/20 border-white/10 hover:border-white/20 hover:bg-dark-secondary/30",
      onDrop && "cursor-pointer",
      className
    );
  }, [variant, isDraggingOver, onDrop, className]);

  // Loading state: show spinner and loading text
  if (variant === 'loading') {
    return (
      <div 
        className={containerClasses}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel || loadingText || 'Loading'}
        aria-labelledby={ariaLabelledBy}
      >
        <EmptyStateIcon 
          icon={icon}
          variant="loading"
          isDraggingOver={false}
        />
        {loadingText && (
          <EmptyStateTitle 
            title={loadingText}
            variant="loading"
            id={ariaLabelledBy}
          />
        )}
        {description && (
          <EmptyStateDescription 
            description={description}
            variant="loading"
          />
        )}
      </div>
    );
  }

  return (
    <div 
      className={containerClasses}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel || title}
      aria-labelledby={ariaLabelledBy}
    >
      <EmptyStateIcon 
        icon={icon}
        variant={variant}
        isDraggingOver={isDraggingOver}
        errorType={errorType}
      />
      
      <EmptyStateTitle 
        title={title}
        variant={variant}
        id={ariaLabelledBy}
      />
      
      <EmptyStateDescription 
        description={description}
        variant={variant}
      />
      
      <EmptyStateActions
        action={action}
        secondaryAction={secondaryAction}
        variant={variant}
        onRetry={onRetry}
        retryLabel={retryLabel}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

// ============================================================================
// PRESET COMPONENTS (Common use cases)
// ============================================================================

// Empty States
export const EmptyFileList = ({ onUpload }: { onUpload: () => void }) => (
  <EmptyState
    icon={FileAudio}
    title="No files yet"
    description="Upload your first audio files to get started."
    action={{
      label: "Upload files",
      onClick: onUpload,
      'aria-label': 'Upload your first files'
    }}
  />
);

export const EmptySearchResults = ({ query }: { query: string }) => (
  <EmptyState
    icon={Search}
    title="No results found"
    description={`No files found for "${query}". Try different search terms, check your spelling, or search by tags, BPM, key, or file type.`}
    aria-label={`No search results for ${query}`}
  />
);

export const EmptyFolder = ({ onUpload }: { onUpload: () => void }) => (
  <EmptyState
    icon={Folder}
    title="Folder is empty"
    description="This folder does not contain any files yet."
    action={{
      label: "Add files",
      onClick: onUpload,
      'aria-label': 'Add files to this folder'
    }}
  />
);

export const EmptyRecentFiles = () => (
  <EmptyState
    title="No files uploaded yet"
    description="Files you've recently uploaded will appear here."
    aria-label="No recent files"
  />
);

export const EmptyVersionResults = ({ 
  searchQuery, 
  onResetSearch 
}: { 
  searchQuery: string;
  onResetSearch?: () => void;
}) => (
  <EmptyState
    icon={GitBranch}
    title="No results found"
    description={`No tracks found for "${searchQuery}". Try different search terms.`}
    secondaryAction={onResetSearch ? {
      label: "Reset search",
      onClick: onResetSearch,
      'aria-label': 'Reset search and show all tracks'
    } : undefined}
    aria-label={`No search results for ${searchQuery}`}
    className="py-8"
  />
);

export const EmptyTracks = ({ onUpload }: { onUpload?: () => void }) => (
  <EmptyState
    icon={GitBranch}
    title="No tracks yet"
    description="Upload your first track to get started."
    action={onUpload ? {
      label: "Upload track",
      onClick: onUpload,
      'aria-label': 'Upload your first track'
    } : undefined}
    aria-label="No tracks available"
    className="py-8"
  />
);

// Empty Cloud Explorer - With drag & drop support
interface EmptyCloudExplorerProps {
  onUpload: () => void;
  onCreateFolder?: () => void;
  isDraggingOver?: boolean;
  currentFolder?: string | null;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

export const EmptyCloudExplorer: React.FC<EmptyCloudExplorerProps> = ({ 
  onUpload, 
  onCreateFolder, 
  isDraggingOver = false,
  currentFolder,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const title = isDraggingOver 
    ? 'Drop files here to upload' 
    : currentFolder 
      ? 'This folder is empty' 
      : 'No files yet';
  
  const description = isDraggingOver
    ? 'Release to start uploading your files'
    : currentFolder 
      ? 'Drag & drop files here or click upload button'
      : 'Drag & drop files here or click upload to get started';

  return (
    <EmptyState
      icon={UploadIcon}
      title={title}
      description={description}
      action={{
        label: "Upload Files",
        onClick: onUpload,
        'aria-label': 'Upload files to cloud explorer'
      }}
      secondaryAction={!currentFolder && onCreateFolder ? {
        label: "Create Folder",
        onClick: onCreateFolder,
        icon: FolderPlus,
        'aria-label': 'Create a new folder'
      } : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      isDraggingOver={isDraggingOver}
      className="py-20"
    />
  );
};

// ============================================================================
// ERROR STATE PRESETS
// ============================================================================

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  errorType?: ErrorType;
  className?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  description,
  onRetry,
  retryLabel = 'Try again',
  errorType = 'generic',
  className
}) => {
  const getErrorContent = () => {
    switch (errorType) {
      case 'network':
        return {
          title: title || 'Connection Error',
          description: description || 'Unable to connect to the server. Please check your internet connection and try again.'
        };
      case 'server':
        return {
          title: title || 'Server Error',
          description: description || 'Something went wrong on our end. Please try again in a moment.'
        };
      case 'notFound':
        return {
          title: title || 'Not Found',
          description: description || 'The requested resource could not be found.'
        };
      default:
        return {
          title: title || 'Something went wrong',
          description: description || 'An unexpected error occurred. Please try again.'
        };
    }
  };

  const content = getErrorContent();

  return (
    <EmptyState
      variant="error"
      title={content.title}
      description={content.description}
      onRetry={onRetry}
      retryLabel={retryLabel}
      errorType={errorType}
      className={className}
    />
  );
};

export const NetworkError = ({ onRetry }: { onRetry?: () => void }) => (
  <ErrorState 
    errorType="network" 
    onRetry={onRetry}
  />
);

export const ServerError = ({ onRetry }: { onRetry?: () => void }) => (
  <ErrorState 
    errorType="server" 
    onRetry={onRetry}
  />
);

export const NotFoundError = ({ onRetry }: { onRetry?: () => void }) => (
  <ErrorState 
    errorType="notFound" 
    onRetry={onRetry}
  />
);

// ============================================================================
// LOADING STATE PRESET
// ============================================================================

interface LoadingStateProps {
  text?: string;
  description?: string;
  className?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  text = 'Loading...',
  description,
  className
}) => (
  <EmptyState
    variant="loading"
    title={text}
    description={description}
    loadingText={text}
    className={className}
    aria-label={text}
  />
);
